"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

export const HomeworkCard = () => {
  const { t } = useTranslation(["workspaces", "common"]);

  // Mock homework data - REMOVED, now empty for demonstration
  const homeworkItems: any[] = []; // This will now be an empty array

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "medium":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "low":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "in_progress":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "overdue":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-foreground/50 h-fit rounded-2xl border-none p-4 pe-2 backdrop-blur-xl sm:p-6 sm:pe-2"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.4 }}
    >
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <Icon
            className="text-accent-foreground"
            height="1.25rem"
            icon="mdi:book-edit-outline"
            width="1.25rem"
          />
          <h3 className="text-accent-foreground text-base font-semibold sm:text-lg">
            {t("workspaces:dashboard.homework.title")}
          </h3>
        </div>
        <Badge
          className="bg-muted/30 text-muted-foreground border-border/50 text-xs"
          variant="outline"
        >
          {homeworkItems.length}
        </Badge>
      </div>

      {homeworkItems.length === 0 ? (
        <div className="flex h-60 flex-col items-center justify-center text-center sm:h-80">
          <Icon
            className="text-muted-foreground mb-3 sm:mb-4"
            height="2.5rem"
            icon="mdi:book-off-outline"
            width="2.5rem"
          />
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("workspaces:dashboard.homework.noHomework")}
          </p>
        </div>
      ) : (
        <ScrollArea className="h-60 sm:h-80">
          <div className="space-y-2 pe-2 sm:space-y-3 sm:pe-4">
            {homeworkItems.map((item, index) => (
              <motion.div
                key={item.id}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-muted/20 hover:bg-muted/30 group relative cursor-pointer overflow-hidden rounded-lg p-3 transition-all duration-300 sm:rounded-xl sm:p-4"
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                {/* Decorative circle */}
                <div
                  className={`absolute -top-4 -right-4 h-12 w-12 rounded-full ${
                    item.priority === "high"
                      ? "bg-gradient-to-br from-red-400/20 to-orange-500/20"
                      : item.priority === "medium"
                        ? "bg-gradient-to-br from-yellow-400/20 to-orange-500/20"
                        : "bg-gradient-to-br from-green-400/20 to-blue-500/20"
                  }`}
                />
                <div className="absolute top-1 right-1 flex w-fit flex-col items-end gap-1 sm:gap-2">
                  <Badge
                    className={`px-2 py-0.5 text-[0.65rem] sm:text-xs ${getPriorityColor(item.priority)}`}
                    variant="outline"
                  >
                    {t(
                      //@ts-ignore
                      `workspaces:dashboard.homework.priority.${item.priority}`,
                    )}
                  </Badge>
                  <Badge
                    className={`px-2 py-0.5 text-[0.65rem] sm:text-xs ${getStatusColor(item.status)}`}
                    variant="outline"
                  >
                    {t(
                      //@ts-ignore
                      `workspaces:dashboard.homework.status.${item.status}`,
                    )}
                  </Badge>
                </div>
                <div className="mb-2 flex flex-col items-start justify-between pr-20 sm:mb-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-accent-foreground mb-0.5 truncate text-xs font-medium sm:mb-1 sm:text-sm">
                      {item.title}
                    </h4>
                    <p className="text-muted-foreground text-[0.65rem] sm:text-xs">
                      {item.subject}
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <Icon
                        className="text-muted-foreground"
                        height="0.875rem"
                        icon="mdi:calendar-outline"
                        width="0.875rem"
                      />
                      <span className="text-muted-foreground text-[0.65rem] sm:text-xs">
                        {t("workspaces:dashboard.homework.dueDate")}:{" "}
                        {format(item.dueDate, "MMM d")}
                      </span>
                    </div>
                    <Icon
                      className="text-muted-foreground group-hover:text-accent-foreground transition-colors"
                      height="0.875rem"
                      icon="mdi:chevron-right"
                      width="0.875rem"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ScrollArea>
      )}

      <div className="border-border/50 mt-3 border-t pt-3 sm:mt-4 sm:pt-4">
        <Button className="w-full text-xs sm:text-sm" size="sm" variant="foreground">
          <Icon className="mr-2" height="0.875rem" icon="mdi:plus" width="0.875rem" />
          {t("workspaces:dashboard.homework.viewAll")}
        </Button>
      </div>
    </motion.div>
  );
};
